import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";

export const scrollToAnchor = (anchor) => {
  if (anchor === "top") {
    if (window.__lenis) window.__lenis.scrollTo(0);
    else window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }
  const el = document.getElementById(anchor);
  if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -76 });
  else if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const NAV = [
  { label: "Accueil", anchor: "top", testid: "nav-accueil" },
  { label: "Nos matériaux", anchor: "materiaux", testid: "nav-materiaux" },
  { label: "Notre approche", anchor: "approche", testid: "nav-approche" },
  { label: "Votre besoin", anchor: "votre-besoin", testid: "nav-votre-besoin" },
  { label: "Recrutement", to: "/recrutement", testid: "nav-recrutement" },
  { label: "Contact", anchor: "contact", testid: "nav-contact" },
];

export const Logo = ({ dark = false }) => (
  <Link
    to="/"
    data-testid="logo-equoris"
    className="flex items-center gap-3 shrink-0 select-none"
    aria-label="EQUORIS — Accueil"
  >
    <img src="/assets/logo-mark.png" alt="" className="h-9 w-auto" />
    <span
      className={`text-base sm:text-lg font-semibold tracking-[0.42em] ${
        dark ? "text-ivoire" : "text-petrol"
      }`}
    >
      EQUORIS
    </span>
  </Link>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const go = (item) => (e) => {
    if (item.to) return;
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: item.anchor } });
    } else {
      scrollToAnchor(item.anchor);
    }
  };

  const goCommercial = (e) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") navigate("/", { state: { scrollTo: "votre-besoin" } });
    else scrollToAnchor("votre-besoin");
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled || open
          ? "bg-ivoire/90 backdrop-blur-md border-b border-petrol/10 shadow-[0_1px_0_rgba(13,44,56,0.04)]"
          : "bg-ivoire/60 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <div className="flex items-center justify-between h-[72px]">
          <Logo />
          <nav className="hidden lg:flex items-center gap-7" aria-label="Navigation principale">
            {NAV.map((item) =>
              item.to ? (
                <Link
                  key={item.testid}
                  to={item.to}
                  data-testid={item.testid}
                  className={`text-[13px] font-medium tracking-wide transition-colors duration-200 hover:text-teal ${
                    location.pathname === item.to ? "text-teal" : "text-encre/80"
                  }`}
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.testid}
                  href={item.anchor === "top" ? "/" : `#${item.anchor}`}
                  onClick={go(item)}
                  data-testid={item.testid}
                  className="text-[13px] font-medium tracking-wide text-encre/80 transition-colors duration-200 hover:text-teal"
                >
                  {item.label}
                </a>
              )
            )}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="/plaquette-commerciale-equoris.pdf"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-plaquette-link"
              className="text-[13px] font-medium text-petrol/80 hover:text-teal transition-colors duration-200 inline-flex items-center gap-1"
            >
              Notre plaquette
              <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
            <button
              onClick={goCommercial}
              data-testid="header-cta-commercial"
              className="ml-2 rounded-full bg-petrol text-ivoire text-[13px] font-semibold px-5 py-2.5 transition-all duration-300 hover:bg-teal hover:-translate-y-0.5"
            >
              Demander un commercial
            </button>
          </div>
          <button
            className="lg:hidden text-petrol p-2"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-button"
            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {open && (
        <div
          className="lg:hidden bg-ivoire border-b border-petrol/10 px-6 pt-2 pb-6 space-y-1"
          data-testid="mobile-menu"
        >
          {NAV.map((item) =>
            item.to ? (
              <Link
                key={item.testid}
                to={item.to}
                data-testid={`mobile-${item.testid}`}
                className="block py-2.5 text-sm font-medium text-encre/85"
              >
                {item.label}
              </Link>
            ) : (
              <a
                key={item.testid}
                href={item.anchor === "top" ? "/" : `#${item.anchor}`}
                onClick={go(item)}
                data-testid={`mobile-${item.testid}`}
                className="block py-2.5 text-sm font-medium text-encre/85"
              >
                {item.label}
              </a>
            )
          )}
          <a
            href="/plaquette-commerciale-equoris.pdf"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mobile-plaquette-link"
            className="block py-2.5 text-sm font-medium text-teal"
          >
            Notre plaquette
          </a>
          <button
            onClick={goCommercial}
            data-testid="mobile-cta-commercial"
            className="mt-3 w-full rounded-full bg-petrol text-ivoire text-sm font-semibold px-5 py-3"
          >
            Demander un commercial
          </button>
        </div>
      )}
    </header>
  );
};
